'use client';

import { LoadingDark } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import {
  COUPON_ERROR_CODES,
  type CouponGoodsItemDto,
  isApiError,
  isUnauthorizedErrorCode,
  receiveCouponService,
} from '@musinsa/services-curator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { createLoginUrl } from '../../common/auth/redirectToLogin';
import { FloatingButtonGroup, ScrollToTopButton } from '../../common/components';
import { useCuratorSnackbar, useNavigate } from '../../common/hooks';
import { pathsMusinsa } from '../../common/paths/musinsa';
import { curatorQueries } from '../../query-factory';
import { PENDING_COUPON_DIALOG_QUERY_KEY } from '../../shop/components/ShopCouponPendingDialog';
import {
  buildCouponGoodsTitleImpressionGtmAttr,
  SCREEN_COUPON_GOODS_VISITOR,
  trackCouponGoodsCuratorShopClickMusinsa,
  trackCouponGoodsProductImpressionMusinsa,
  trackVisitorCouponGoodsCouponDownloadClickMusinsa,
  trackVisitorCouponGoodsProductClickMusinsa,
  trackVisitorCouponGoodsScreenViewMusinsa,
} from '../utils/couponGoodsMusinsaAnalytics';
import { CouponGoodsList } from './CouponGoodsList';
import { CouponGuideBottomSheet } from './CouponGuideBottomSheet';
import { VisitorCouponHeaderCard } from './VisitorCouponHeaderCard';

interface VisitorCouponGoodsPageProps {
  shopSlug: string;
  couponNo: number;
  className?: string;
}

const RECEIVE_FAILURE_MESSAGE = '쿠폰 발급에 실패하였습니다. 다시 시도해주세요.';

// FE 고정 문구 (useReceiveCoupon 과 동일 정책 — UI/UX 요구사항에 따라 BE 메시지와 달리 표시)
const FE_FIXED_MESSAGES: Record<string, string> = {
  [COUPON_ERROR_CODES.ALREADY_PUBLISHED]: '이미 발급받은 쿠폰입니다.',
  [COUPON_ERROR_CODES.LIMIT_EXCEEDED]: '준비된 쿠폰이 모두 소진되었습니다.',
};

export const VisitorCouponGoodsPage = ({
  shopSlug,
  couponNo,
  className,
}: VisitorCouponGoodsPageProps) => {
  const queryClient = useQueryClient();
  const snackbar = useCuratorSnackbar();
  const { navigate, navigateReplace } = useNavigate();

  const goodsQuery = curatorQueries.coupon.goods({ couponNo });
  // 진행 예정 쿠폰은 BE 가 403 COUPON_NOT_STARTED 로 응답한다. 전역 onError 가 모든 403 을
  // intro 로 리다이렉트하므로 skipGlobalErrorHandler 로 우회한 뒤 아래에서 직접 분기.
  const { data, error } = useQuery({
    ...goodsQuery,
    meta: { skipGlobalErrorHandler: true },
  });

  const { mutate: receiveCoupon, isPending: isReceiving } = useMutation({
    mutationFn: () => receiveCouponService({ shopTitle: shopSlug, couponNo }),
    onSuccess: () => {
      queryClient.setQueryData(goodsQuery.queryKey, (old: typeof data) =>
        old ? { ...old, status: 'RECEIVED' as const } : old,
      );
      snackbar.success('쿠폰이 발급되었습니다.');
    },
    onError: (error) => {
      if (!isApiError(error)) {
        snackbar.error(RECEIVE_FAILURE_MESSAGE);
        return;
      }

      const errorCode = error.data?.meta?.errorCode;
      const beMessage = error.data?.meta?.message;

      // 비로그인(401) — 토스트 미노출, 로그인 화면으로 직접 이동
      if (isUnauthorizedErrorCode(errorCode)) {
        navigate(createLoginUrl(window.location.href));
        return;
      }

      // ALREADY_PUBLISHED / LIMIT_EXCEEDED 는 FE 고정 문구 사용 (useReceiveCoupon 동일)
      if (
        errorCode === COUPON_ERROR_CODES.ALREADY_PUBLISHED ||
        errorCode === COUPON_ERROR_CODES.LIMIT_EXCEEDED
      ) {
        snackbar.error(FE_FIXED_MESSAGES[errorCode]);
        return;
      }

      snackbar.error(beMessage || RECEIVE_FAILURE_MESSAGE);
    },
  });

  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;

  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const hasRedirectedRef = useRef(false);

  const couponType = data ? String(data.salesRate) : '';

  useEffect(() => {
    if (!isMusinsa || !data) return;
    trackVisitorCouponGoodsScreenViewMusinsa({
      brandId: data.brandId,
      couponId: couponNo,
      couponName: data.couponName,
      couponType,
      curatorId: shopSlug,
      fromScreen: SCREEN_COUPON_GOODS_VISITOR,
    });
  }, [isMusinsa, data, couponNo, shopSlug, couponType]);

  const handleClickShop = useCallback(() => {
    if (isMusinsa && data) {
      trackCouponGoodsCuratorShopClickMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_VISITOR,
      });
    }
    navigate(pathsMusinsa.shop({ snsTitle: shopSlug }));
  }, [isMusinsa, data, navigate, shopSlug, couponNo, couponType]);

  // 시트 5 row 13 spec 의 button_name=copy_link 는 카피페이스트 실수 — 실제 액션은 "쿠폰 받기".
  // mss `coupon_download` 페어리티에 따라 button_name='coupon_download' 로 발화 (visitor 전용).
  const handleReceive = useCallback(() => {
    if (isMusinsa && data) {
      trackVisitorCouponGoodsCouponDownloadClickMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_VISITOR,
      });
    }
    receiveCoupon();
  }, [isMusinsa, data, receiveCoupon, shopSlug, couponNo, couponType]);

  const handleProductImpression = useCallback(
    (item: CouponGoodsItemDto) => {
      if (!data) return;
      trackCouponGoodsProductImpressionMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_VISITOR,
        productId: item.goodsNo,
      });
    },
    [data, couponNo, shopSlug, couponType],
  );

  const handleClickProduct = useCallback(
    (item: CouponGoodsItemDto) => {
      if (!data) return;
      trackVisitorCouponGoodsProductClickMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_VISITOR,
        productId: item.goodsNo,
        productName: item.goodsName,
      });
    },
    [data, couponNo, shopSlug, couponType],
  );

  // 판매자(owner)가 visitor 경로(`/curator/s/...`)로 들어온 경우 myshop 경로로 redirect.
  const shouldRedirectToMyShop = Boolean(data?.isOwner);

  // 방문자가 진행 예정 쿠폰 페이지로 진입한 경우 큐레이션 샵으로 redirect 하고
  // shop 페이지에서 query string 플래그(`fromPendingCoupon=true`)를 보고 안내 다이얼로그를 띄운다.
  // 정상 응답(`isPending=true`) 외에, 시작 전 쿠폰은 BE 가 403 COUPON_NOT_STARTED 로 차단하므로 동일 처리.
  const isCouponNotStartedError =
    isApiError(error) && error.data?.meta?.errorCode === COUPON_ERROR_CODES.NOT_STARTED;
  const shouldRedirectToShop = Boolean(
    (data && !data.isOwner && data.isPending) || isCouponNotStartedError,
  );

  useEffect(() => {
    if (hasRedirectedRef.current) return;
    if (shouldRedirectToMyShop) {
      hasRedirectedRef.current = true;
      navigateReplace(pathsMusinsa.myShopCouponGoods({ shopSlug, couponNo }));
      return;
    }
    if (shouldRedirectToShop) {
      hasRedirectedRef.current = true;
      const fallbackPath = pathsMusinsa.shop({
        snsTitle: shopSlug,
        query: { [PENDING_COUPON_DIALOG_QUERY_KEY]: true },
      });
      (async () => {
        try {
          // NOTE: BE 가 발급한 originUrl 사용 → UTM 파라미터 / 플랫폼별 query 보존.
          // X-Platform 헤더로 BE 가 자동 플랫폼 감지 (FE 분기 X).
          const { originUrl } = await queryClient.fetchQuery(
            curatorQueries.link.getCuratorShopLink({ snsTitle: shopSlug }),
          );
          const url = new URL(originUrl);
          url.searchParams.set(PENDING_COUPON_DIALOG_QUERY_KEY, 'true');
          navigateReplace(`${url.pathname}${url.search}`);
        } catch {
          // 404 (큐레이터샵/링크 없음) / 400 (unsupported platform) — 기존 path builder 폴백.
          navigateReplace(fallbackPath);
        }
      })();
    }
  }, [
    shouldRedirectToMyShop,
    shouldRedirectToShop,
    navigateReplace,
    queryClient,
    shopSlug,
    couponNo,
  ]);

  // 초기 fetch / redirect 대기 중에는 빈 화면이 멈춰있는 것처럼 보이므로 로딩 인디케이터 노출.
  if (!data || shouldRedirectToMyShop || shouldRedirectToShop) {
    return (
      <div
        className={twMerge('flex flex-1 grow items-center justify-center', className)}
        role="status"
        aria-busy="true"
      >
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  // GA4 impression_content — className + data-* 모두 헤더 컨테이너에 박아야 GTM 매칭 정상.
  const titleGtm = isMusinsa
    ? buildCouponGoodsTitleImpressionGtmAttr({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_VISITOR,
      })
    : null;
  const { className: titleGtmClassName, ...titleGtmDataAttrs } = titleGtm ?? {
    className: undefined,
  };

  return (
    <div className={twMerge('flex grow flex-col bg-gray-100 safe-area-bottom', className)}>
      <div {...titleGtmDataAttrs} className={titleGtmClassName}>
        <VisitorCouponHeaderCard
          data={data}
          isReceiving={isReceiving}
          onClickShop={handleClickShop}
          onClickGuide={() => setIsGuideOpen(true)}
          onReceive={handleReceive}
        />
      </div>
      <CouponGoodsList
        couponNo={couponNo}
        isOwner={false}
        coupon={{ couponId: couponNo, couponName: data.couponName }}
        onProductImpression={isMusinsa ? handleProductImpression : undefined}
        onClickProduct={isMusinsa ? handleClickProduct : undefined}
      />
      <CouponGuideBottomSheet open={isGuideOpen} onOpenChange={setIsGuideOpen} />
      <FloatingButtonGroup.Layout className="flex-col items-end safe-area-bottom">
        <FloatingButtonGroup.Container>
          <ScrollToTopButton />
        </FloatingButtonGroup.Container>
      </FloatingButtonGroup.Layout>
    </div>
  );
};
